import { ExternalLink, FileText, AlertCircle, CheckCircle, RefreshCw } from 'lucide-react';
import ComponentCard from '../common/ComponentCard';

interface OnboardingUrl {
    type: string;
    title: string;
    url: string;
    expirationDate: string;
}

interface KycDocumentsCardProps {
    hasPendingDocuments: boolean;
    isKycComplete: boolean;
    onboardingUrls?: OnboardingUrl[];
    loading?: boolean;
    onRefresh?: () => void;
}

export default function KycDocumentsCard({ 
    hasPendingDocuments, 
    isKycComplete,
    onboardingUrls = [],
    loading = false,
    onRefresh
}: KycDocumentsCardProps) {
    
    if (loading) {
        return (
            <ComponentCard title="Identidade">
                <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Verificando status...</p>
                </div>
            </ComponentCard>
        );
    }

    // KYC Completo
    if (isKycComplete && !hasPendingDocuments) {
        return (
            <ComponentCard title="Identidade">
                <div className="p-8">
                    <div className="flex flex-col items-center justify-center text-center">
                        <div className="w-16 h-16 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center mb-4">
                            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-500" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-2">
                            Identidade Verificada
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">
                            Sua identidade foi verificada com sucesso. Todos os documentos necessários foram aprovados.
                        </p>
                    </div>
                </div>
            </ComponentCard>
        );
    }

    // Documentos Pendentes
    return (
        <ComponentCard title="Identidade">
            <div className="p-6">
                {/* Alerta de Documentos Pendentes */}
                <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <h4 className="text-sm font-semibold text-yellow-800 dark:text-yellow-300 mb-1">
                                Documentos Pendentes
                            </h4>
                            <p className="text-sm text-yellow-700 dark:text-yellow-400">
                                {onboardingUrls.length > 0 ? (
                                    <>
                                        Sua empresa possui {onboardingUrls.length} documento{onboardingUrls.length > 1 ? 's' : ''} pendente{onboardingUrls.length > 1 ? 's' : ''} de verificação.
                                        Envie os documentos para completar a verificação de identidade.
                                    </>
                                ) : (
                                    <>
                                        Sua empresa possui documentos pendentes de verificação.
                                        Os links de envio estão sendo gerados. Por favor, aguarde.
                                    </>
                                )}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Lista de Documentos */}
                {onboardingUrls.length > 0 ? (
                    <div className="space-y-4">
                        {onboardingUrls.map((doc, index) => {
                        const expirationDate = new Date(doc.expirationDate);
                        const isExpiringSoon = expirationDate.getTime() - Date.now() < 7 * 24 * 60 * 60 * 1000; // 7 dias

                        return (
                            <div 
                                key={`${doc.type}-${index}`}
                                className="p-4 border border-gray-200 dark:border-gray-800 rounded-lg hover:border-blue-300 dark:hover:border-blue-700 transition-colors"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex items-start gap-3 flex-1">
                                        <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FileText className="w-5 h-5 text-blue-600 dark:text-blue-500" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h5 className="text-sm font-semibold text-gray-800 dark:text-white/90 mb-1">
                                                {doc.title}
                                            </h5>
                                            <div className="flex items-center gap-2">
                                                <span className={`text-xs ${isExpiringSoon ? 'text-red-600 dark:text-red-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                                                    Válido até: {expirationDate.toLocaleDateString('pt-BR')}
                                                </span>
                                                {isExpiringSoon && (
                                                    <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-xs rounded-full font-medium">
                                                        Expira em breve
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <a
                                        href={doc.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex-shrink-0"
                                    >
                                        Enviar Documento
                                        <ExternalLink className="w-4 h-4" />
                                    </a>
                                </div>
                            </div>
                        );
                    })}
                    </div>
                ) : (
                    <div className="p-8 text-center">
                        <div className="w-16 h-16 bg-yellow-100 dark:bg-yellow-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                            <AlertCircle className="w-8 h-8 text-yellow-600 dark:text-yellow-500" />
                        </div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90 mb-2">
                            Links em Processamento
                        </h4>
                        <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-4">
                            Os links de envio de documentos estão sendo gerados pelo sistema.
                            Por favor, aguarde alguns instantes e recarregue esta página.
                        </p>
                        {onRefresh && (
                            <button
                                onClick={onRefresh}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                            >
                                <RefreshCw className="w-4 h-4" />
                                Recarregar Status
                            </button>
                        )}
                    </div>
                )}

                {/* Informação Adicional */}
                {onboardingUrls.length > 0 && (
                    <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                            <strong>Importante:</strong> Certifique-se de enviar os documentos para que sua conta seja verificada e possa ser utilizada normalmente.
                        </p>
                    </div>
                )}
            </div>
        </ComponentCard>
    );
}
